/**
 * Every functional string in the customer-facing app, in English.
 *
 * This file is the SOURCE. `ne.ts` is a partial of it, and anything missing
 * there falls back to here rather than to a gap — so a key added today is
 * readable in both languages today and translated whenever the translation
 * arrives.
 *
 * Marketing copy is NOT here. The landing page's prose lives in `site.ts` and
 * is the institute's own voice — twenty years of positioning, a director's
 * message, a scholarship pitch. That needs a Nepali writer, not a developer
 * with a dictionary, so it is kept separate and translated separately.
 *
 * Keys are `area.thing`, flat rather than nested: a translator opening this
 * file should be able to see every string at once without unfolding anything,
 * and a flat key greps cleanly to its use site.
 */
export const en = {
  // ----------------------------------------------------------------- common
  'common.save': 'Save changes',
  'common.saving': 'Saving…',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.edit': 'Edit',
  'common.loading': 'Loading…',
  'common.back': 'Back',

  // A date picked in Bikram Sambat (BsDateInput).
  'bsdate.day': 'Day',
  'bsdate.month': 'Month',
  'bsdate.year': 'Year (BS)',
  'bsdate.time': 'Time',
  'bsdate.ad': '{date} AD',
  'bsdate.incomplete': 'Choose the day, the month and the year.',
  'bsdate.before_min': 'Choose {date} or later.',
  'bsdate.after_max': 'Choose {date} or earlier.',
  'common.error_title': 'Something went wrong',
  'common.error_body': 'This part of the page could not be shown. Reload to try again — nothing you have already saved is lost.',
  'common.error_load': 'Something went wrong while loading this. Please try again.',
  'common.reload': 'Reload the page',
  /* The "i" beside a field's label (TipField). Its name, not its text: what
     it says is the field's own hint, read as the field's description. */
  'field.more_about': 'More about {field}',

  // -------------------------------------------------------------- navigation
  'nav.menu': 'Menu',
  'nav.you': 'You',
  'nav.dashboard': 'Dashboard',
  'nav.my_tests': 'My tests',
  'nav.my_results': 'My results',
  'nav.my_plan': 'My plan',
  'nav.billing': 'Billing',
  'nav.my_profile': 'My profile',
  'nav.help': 'Help',
  'nav.collapse': 'Collapse the menu',
  'nav.expand': 'Expand the menu',
  'nav.open_menu': 'Open the menu',
  'nav.close_menu': 'Close the menu',
  'nav.tests': 'Tests',
  'nav.results': 'Results',
  'nav.bill_due': 'Due now',
  'nav.bill_plans': 'Plans',
  'nav.bill_invoices': 'Invoices',
  'nav.bill_activity': 'Activity',
  'footer.label': 'Footer',
  'footer.rights': '© {year} Career Academy. All rights reserved.',

  // ------------------------------------------------------------------ header
  'header.tagline': 'Weekly Assessment Platform',
  'header.sign_out': 'Sign out',
  'header.admin_console': 'Admin console',
  'header.mobile_unverified': 'mobile unverified',
  'header.language': 'Language',

  // ----------------------------------------------------------------- roles
  'role.parent': 'Parent',
  'role.student': 'Student',
  'role.owner': 'Owner',
  'role.finance': 'Finance',
  'role.academic': 'Academic',
  'role.reviewer': 'Reviewer',
  'role.support': 'Support',
  'role.signed_in': 'Signed in',

  // ---------------------------------------------------------- notifications
  'notif.title': 'Notifications',
  'notif.mark_all_read': 'Mark all read',
  'notif.empty': 'Nothing yet. Reminders and results will appear here.',
  'notif.see_all': 'See all notifications',
  'notif.page_title': 'Notifications',
  'notif.unread_count': '{n} you have not read yet.',
  'notif.all_read': 'Everything here has been read.',
  'notif.everything_sent': 'Everything we have sent you',
  'notif.page_empty': 'Nothing yet. Reminders, results and receipts will be kept here.',
  // What the row says it will open. A single "Open" for every destination is
  // how somebody clicks a security alert expecting a receipt — see
  // `destinationOf` in lib/notification-link.ts.
  'notif.open': 'Open',
  'notif.to_open': 'Open',
  'notif.to_scorecard': 'Scorecard',
  'notif.to_invoices': 'Invoices',
  'notif.to_billing': 'Billing',
  'notif.to_account': 'Your devices',
  'notif.to_tests': 'Tests',
  'notif.to_support': 'Support',
  'notif.to_dashboard': 'Dashboard',
  'notif.more': 'More',
  'notif.less': 'Less',
  'notif.choose_channels':
    'Choose which of these reach you by email, SMS or WhatsApp in your account.',

  // when(): how long ago something arrived
  'when.just_now': 'just now',
  'when.minutes': '{n} min ago',
  'when.hours': '{n}h ago',
  'when.yesterday': 'yesterday',
  'when.days': '{n} days ago',

  // ------------------------------------------- a student's own plan (074)
  'sub.card_for': "{name}'s plan.",
  'sub.card_shared': "{name}'s plan, shared with {others}. Each child on it is charged.",
  'sub.card_family': "Your plans, and what each one covers.",
  'sub.per_child': "{n} children × {each}",
  'splan.title': 'My plan',
  'splan.lede': 'Choose your plan and pay for it here. It goes on your family account, so {parent} sees it and gets the receipt.',
  'splan.ask_title': 'Ask your parent',
  'splan.too_young': 'Students choose and pay for their own plan from Class {n}. Ask {parent} to do it from their account.',
  'splan.choose_title': 'Choose a plan',
  'splan.choose_sub': 'You can pay once you have chosen. Prices include VAT.',
  'splan.cycle_monthly': 'Monthly',
  'splan.cycle_annual': 'Yearly',
  'splan.no_plans': 'No plan is on sale for your class right now. Ask the office.',
  'splan.choose_btn': 'Choose this plan',
  'splan.yours_title': 'Your plan',
  'splan.incl_registration': 'includes the one-time {amount} registration fee',
  'splan.parent_mobile': '{parent} needs to verify their mobile number before this can be paid — renewal reminders are sent to it.',
  'splan.family_note': 'The invoice and receipt go to {parent}, who can also see and change this plan.',
  'splan.pending': 'The gateway says the payment is still pending. Nothing has been charged yet — check again in a few minutes.',
  'splan.pay_link': 'Choose a plan and pay',
  'splan.paid_up': "You're paid up until {date}. You can pay for the next month in its last week.",

  // ---------------------------------------- landing announcement (pop-up)
  'pop.close': 'Close announcement',
  'pop.tagline': 'Celebrating Today, Inspiring Tomorrow',
  'pop.anniv_number': '25',
  'pop.anniv_suffix': 'th',
  'pop.anniv_title': 'ANNIVERSARY',
  'pop.anniv_of': 'OF CAREER ACADEMY',
  'pop.trust': '25 YEARS OF TRUST, EXCELLENCE & COMMITMENT TO EDUCATION',
  'pop.logo_alt': 'CST Nepal — Career Scholarship Test',
  'pop.banner_title': 'CAREER SCHOLARSHIP TEST  |  CLASSES 1–12',
  'pop.banner_sub': 'Test your talent, earn your scholarship, build your future.',
  'pop.why_title': 'WHY JOIN CST?',
  'pop.b1_label': 'Scholarships:',
  'pop.b1': 'win attractive scholarships and rewards',
  'pop.b2_label': 'Assess your potential:',
  'pop.b2': 'evaluate your academic strengths',
  'pop.b3_label': 'National recognition:',
  'pop.b3': 'compete with students across Nepal',
  'pop.b4_label': 'Certificates and awards:',
  'pop.b4': 'merit certificates for top performers',
  'pop.details_title': 'TEST DETAILS',
  'pop.when': 'When',
  'pop.when_v': 'Every Saturday',
  'pop.time': 'Exam time',
  'pop.time_v': '8:00 AM – 10:00 AM',
  'pop.results': 'Results',
  'pop.results_v': 'Published from 11:00 AM',
  'pop.format': 'Format',
  'pop.format_v': 'Weekly test  |  50 marks  |  50 minutes',
  'pop.get': 'You get',
  'pop.get_v': 'Ranking, analysis and performance report',
  'pop.price_title': 'AFFORDABLE AND ACCESSIBLE',
  'pop.reg_label': 'Initial registration fee',
  'pop.reg_amount': 'NPR 100',
  'pop.monthly_label': 'Monthly payment (4 weeks)',
  'pop.monthly_amount': 'NPR 500',
  'pop.bonus': '★ MONTHLY PACKAGE BONUS: Score 160/200 and win a scholarship for next month! ★',
  'pop.teachers_title': 'BUILT BY EXPERIENCED TEACHERS',
  'pop.teachers': "CST is being built by a team of St. Xavier's ex-teacher Mr. RMJHA, St. Mary's, Pathshala and 22 other reputed school teachers.",
  'pop.register_title': 'REGISTER NOW',
  'pop.phone': 'Phone',
  'pop.email': 'Email',
  'pop.address': 'Career Building, Jawalakhel, Lalitpur',

  // --------------------------------------------------------------- account
  'account.title': 'Account',
  'account.lede': 'Your details, your password, and every device currently signed in.',
  'account.profile_title': 'My profile',
  'account.profile_sub': 'Your name and how we reach you.',
  'account.name': 'Name',
  'account.email': 'Email',
  'account.mobile': 'Mobile',
  'account.address_legend': 'Where your family lives',
  'account.province': 'Province',
  'account.permanent_address': 'Permanent address',
  'account.local_address': 'Local address',
  'account.not_set': 'not set',
  'account.unverified': 'unverified',
  'account.saved': 'Your details are saved.',
  'account.verify_again': 'Changing your mobile means verifying it again.',
  'account.email_change': 'Change email',
  'account.email_change_title': 'Change your email',
  'account.email_change_lede': 'We send a code to the new address. Your email changes only when you enter it — until then you keep signing in with the old one.',
  'account.new_email': 'New email address',
  'account.current_password': 'Current password',
  'account.current_password_hint': 'So we know it is you making this change.',
  'account.send_code': 'Send code',
  'account.sending': 'Sending…',
  'account.code_sent': 'We sent a 6-digit code to {email}. It expires in 30 minutes.',
  'account.code': 'Code',
  'account.confirm_change': 'Confirm change',
  'account.confirming': 'Confirming…',
  'account.cancel_change': 'Cancel the change',
  'account.email_changed': 'Your email is now {email}. We told your old address too.',
  'account.pending_email': 'Waiting for the code sent to {email}.',
  'account.enter_code': 'Enter code',
  'account.email_readonly': 'To change your email, use Change email.',
  'account.mobile_password': 'Changing your mobile needs your current password.',
  'account.details_title': 'Registration',
  'account.details_sub': 'Your numbers and where you study. Ask the office to correct anything here.',
  'account.details_sub_family': 'Your family number, which the office uses to find your account. Ask them to correct anything here.',
  'account.reg_no': 'Registration no.',
  'account.family_no': 'Family no.',
  'account.class': 'Class',
  'account.group': 'Group',
  'account.section': 'Section',
  'account.school': 'School',
  'account.parent': 'Parent',
  'account.family_title': 'Your children',
  'account.no_children': 'No children on this account yet.',
  'account.show_all': 'Show all {n}',
  'account.show_fewer': 'Show fewer',
  'account.subscription_title': 'Subscription',
  'account.no_subscription': 'No subscription yet.',
  'account.renews': 'until {date}',
  'account.manage_billing': 'Billing and receipts',
  'account.activity_title': 'Account activity',
  'account.member_since': 'Member since',
  'account.last_sign_in': 'Last sign-in',
  'account.sign_in_with': 'Signs in with',
  'account.method_password': 'Email and password',
  'account.method_google': 'Google',
  'account.never': 'never',
  'sub.st_trial': 'Free trial',
  'sub.st_active': 'Paid',
  'sub.st_grace': 'Renewal due',
  'sub.st_past_due': 'Payment overdue',
  'sub.st_suspended': 'Suspended',
  'sub.st_paused': 'Paused',
  'sub.st_cancelled': 'Cancelled',
  'sub.st_expired': 'Expired',

  // --------------------------------------------------- notification channels
  'channels.title': 'How we contact you',
  'channels.sub': 'Turn a channel off and the others still reach you.',
  'channels.email': 'Email',
  'channels.email_blurb': 'Receipts, results and renewal reminders. The one we always fall back to.',
  'channels.sms': 'SMS',
  'channels.sms_blurb':
    'Short reminders to your verified mobile. Nothing is sent to an unverified number.',
  'channels.whatsapp': 'WhatsApp',
  'channels.whatsapp_blurb':
    'The same reminders on WhatsApp instead of waiting for an SMS. Off until you ask for it.',
  'channels.in_app': 'In the app',
  'channels.in_app_blurb':
    'Kept in your notification bell, so a message you missed is still there later.',
  'channels.all_off_warning':
    'With email, SMS and WhatsApp all off, nothing will reach you outside the app — '
    + 'including the reminder that your subscription is about to lapse.',

  // --------------------------------------------------------------- student
  'student.hello': 'Hello, {name}',
  'student.next_test': 'Your next test',
  'student.no_test': 'No test is scheduled for your class yet. Check back soon.',
  'student.until_opens': 'until it opens',
  'student.start_test': 'Start the test',
  'student.test_open': 'Your test is open now.',
  'student.finished':
    'You have finished this test. Your result comes out once everyone is done.',
  'student.one_at_a_time':
    'This test shows one question at a time, and you cannot go back — so answer each one '
    + 'before you move on.',
  'student.last_result': 'Your last result',
  'student.see_what_right': 'See what you got right',
  'student.test_history': 'Test history',
  'student.tests_taken': 'Tests taken',
  'student.results_out': 'Results out',
  'student.every_test': 'Every test you have taken',
  'student.back_to_account': 'Back to my account',
  'student.child_last_result': '{name} — last result',
  'student.full_scorecard': 'See the full scorecard',
  'when.today': 'Today',
  'when.tomorrow': 'Tomorrow',
  'when.in_days': 'In {n} days',

  // -------------------------------------------------------------- scorecard
  'score.score': 'Score',
  'score.class_rank': 'Class rank',
  'score.band': 'Band',
  'score.time_used': 'Time used',
  'score.vs_last': 'vs last time',
  'score.accuracy': 'Accuracy',
  'score.by_subject': 'By subject',
  // The chart's accessible name. It reads out the figures rather than
  // describing the picture, because the figures are what the picture is for.
  'score.by_subject_alt':
    'Accuracy by subject on this paper, as a percentage of questions asked: {summary}.',
  'score.correct': '{n} correct',
  'score.missed': '{n} missed',
  'score.lowest_here': 'Lowest here',
  'score.correct_on_paper': '{correct} of {total} correct on this paper',
  'score.improved': '{who} went from {from}% to {to}% since the last paper',
  'score.next_paper': 'Next paper',
  'score.minutes': 'min',

  // ------------------------------------------------------------------- auth
  'auth.sign_in': 'Sign in',
  'auth.create_account': 'Create an account',
  'auth.welcome_back': 'Welcome back! Please sign in to your account.',
  'auth.signing_in': 'Signing in…',
  // The eye on a password box. The accessible name carries the state, so
  // these are the two halves of one switch rather than a label and a hint.
  'auth.password_show': 'Show password',
  'auth.password_hide': 'Hide password',
  'auth.forgot_link': 'Forgot your password?',
  'auth.back_to_sign_in': 'Back to sign in',
  'auth.confirm_password': 'Confirm password',
  // Mirrors the server's rule so the first attempt is not a round trip.
  'auth.password_rules': 'At least 10 characters, with both letters and numbers.',
  'auth.passwords_differ': 'The two passwords do not match.',

  // ---------------------------------------------------------- password reset
  'forgot.title': 'Reset your password',
  'forgot.intro': 'Enter the email you sign in with. If it has an account, a reset code '
    + 'is on its way.',
  'forgot.send_code': 'Send code',
  'forgot.sending': 'Sending…',
  // Conditional on purpose: the server never says whether an account exists.
  'forgot.sent': 'If {email} has an account, a code has been emailed to it. It expires '
    + 'in 30 minutes.',
  'forgot.code': 'Reset code from the email',
  'forgot.new_password': 'New password',
  'forgot.reset': 'Set new password',
  'forgot.resetting': 'Saving…',
  'forgot.resend': 'Send a new code',
  'forgot.done': 'Your password has been changed, and every other device has been '
    + 'signed out. Sign in with the new one.',
  'forgot.dev_code': 'Development code',

  // -------------------------------------------------------- staff invitation
  'invite.title': 'Join Career Academy',
  'invite.intro': 'You have been invited to the staff console. Choose the name colleagues '
    + 'will see and a password only you know.',
  'invite.code': 'Invitation code',
  'invite.code_hint': 'From the invitation email. Opening the link fills this in.',
  'invite.full_name': 'Full name',
  'invite.password': 'Password',
  'invite.accept': 'Accept invitation',
  'invite.accepting': 'Creating your account…',

  // ----------------------------------------------------------- parent cards
  'parent.verify_mobile': 'Verify your mobile',
  'parent.send_code': 'Send code',
  'parent.tests': 'Tests',
  // The four opening tiles. Only what real data supports — the mockup's
  // "14 days left" is absent because /account/billing sends no server time,
  // and "Unlock after payment" is absent because paying unlocks neither a
  // paused nor a cancelled subscription.
  'stat.amount_due': 'Amount due',
  'stat.nothing_due': 'Nothing due',
  'stat.go_billing': 'Go to billing',
  'stat.last_score': 'Last score',
  'stat.view_scorecard': 'View scorecard',
  'stat.no_result': 'No result yet',
  'stat.locked_no_plan': 'No plan covers this child yet.',
  'stat.locked_pay': 'Opens once a payment is verified',
  'stat.locked_state': 'This subscription is {state}',
  'stat.view_details': 'View details',

  // The dashboard hero.
  'dash.morning': 'Good morning,',
  'dash.afternoon': 'Good afternoon,',
  'dash.evening': 'Good evening,',
  'dash.note': 'Practice today for a brighter tomorrow.',
  'dash.note_sub': 'Keep going!',

  /* ---------------------------------------------------- sign-up pending
     The sentence this page did not have. A family who registered and stopped
     read "Nothing due" over four tiles and concluded they were set up — every
     figure correct, because the tiles price SUBSCRIPTIONS and this family had
     none. Both lines name what cannot happen until it is dealt with, because
     "pending" on its own reads as somebody else's paperwork. */
  'pending.register_title': 'Finish signing up',
  'pending.register_body':
    '{who} cannot sit a test until the one-time joining fee is paid. It is charged '
    + 'once per child and never again.',
  'pending.register_cta': 'Pay {amount}',
  'pending.plan_title': 'Registration paid — now choose a plan',
  'pending.plan_body':
    'A plan is what opens the weekly test, the scorecards and the reports. Nothing '
    + 'is charged automatically: you pick the plan and pay when you are ready.',
  'pending.plan_cta': 'Choose a plan',

  // Subject performance.
  'perf.title': 'Subject performance',
  'perf.sub': 'Accuracy across every released paper, weakest first.',
  'perf.none': 'Nothing to show yet — this fills in once a result is released.',
  'perf.full_report': 'View full report',
  'perf.since_last': 'Change is against the paper before it, where both covered the subject.',
  'perf.change_up': 'up {pct}% on the previous paper',
  'perf.change_down': 'down {pct}% on the previous paper',
  'perf.of_questions': '{correct} of {asked} questions right',

  // Performance overview.
  'po.title': 'Performance overview',
  'po.accuracy_label': 'Accuracy on this paper',
  'po.last_test': 'Last test',

  // Upcoming test.
  'up.title': 'Upcoming test',
  // When nothing is still to come, the card names what it is actually showing.
  'up.latest': 'Latest test',
  'up.sub': 'Whether a paper can be started is decided server-side, per paper.',
  'up.view_all': 'View all',
  'up.duration': '{n} min',
  'up.start_by':
    'Start by {time} to get the full {n} minutes — everyone stops when the window closes.',
  'up.linear': 'One question at a time',
  'up.free_nav': 'Move freely between questions',
  'up.next': 'Next test',

  // Tips.
  'tips.title': 'Tips for this week',
  'tips.also_practise': 'Also worth practising',

  'sub.manage': 'Manage',
  'sub.incl_vat': 'includes {vat} VAT',
  'sub.incl_registration': 'includes a one-time {amount} registration fee for {who}',
  'parent.show_all_tests': 'Show all {n} tests',
  'parent.show_fewer_tests': 'Show fewer',
  'parent.no_papers': 'No papers scheduled for this class.',
  'parent.view_scorecard': 'View scorecard',
  'parent.awaiting_release': 'Awaiting release',
  'parent.start_test': 'Start test',
  'parent.window_closed': 'Window closed',
  'parent.done': 'Submitted',
  'parent.choose_child': 'Choose a child',
  'parent.add_child_to_see': 'Add a child to see their tests.',
  'parent.subscription': 'Subscription',
  'parent.no_subscription': 'No subscription yet.',
  /* Choosing a plan from a screen that is not the sign-up wizard. The
     wizard lets a family register and decide the plan later, and until
     now "later" had nowhere to happen. */
  'plan.choose_title': 'Choose a plan',
  'plan.choose_sub':
    'Weekly tests run on a subscription. Siblings go on one plan, and you can '
    + 'change between monthly and yearly whenever you pay.',
  'plan.choose_note':
    'Choosing a plan does not charge you. The payment buttons appear once it '
    + 'is on your account.',
  'plan.no_children': 'Add a child first — a plan covers the children on your account.',
  /* Picking WHICH children. One subscription carries one plan and one
     cycle, so two monthly and two annual is two subscriptions — the card
     re-offers whoever is left rather than pretending one form can do it. */
  'plan.who_for': 'Who is this plan for?',
  'plan.who_note':
    'Children on one plan are billed together. To put some on monthly and '
    + 'others on yearly, choose the first group now and the rest afterwards.',
  'plan.pick_someone': 'Choose at least one child to see the plans that fit them.',
  'plan.now_the_rest': 'Done. Choose a plan for the others below.',

  // ------------------------------------------------------------- subjects
  'subjects.title': 'Your subjects',
  'subjects.sub': 'How each one is going, and which way it is moving.',
  'subjects.strongest': 'Strongest so far: ',
  'subjects.most_room': 'Most room to gain: ',
  'subjects.subject': 'Subject',
  'subjects.latest': 'Latest',
  'subjects.change': 'Change',
  'subjects.tests': 'Tests',

  // -------------------------------------------------------------- listen
  // The audio equivalent, spoken by the phone (A5). Rendered only where the
  // device actually has a voice in the language on screen.
  'listen.play': 'Listen',
  // Spoken, not read off the screen: "7 / 9" said literally is "seven slash
  // nine", and the numbers are the whole reason for listening.
  'sc.spoken': '{name} scored {score} out of {max} in {paper}. That is {pct} percent.',
  'renew.spoken': '{amount} is due for {children}, covering until {date}.',
  'listen.stop': 'Stop',

  // ----------------------------------------------------------- recognition
  // The board is shown only to a signed-in family (B4). Nothing in this block
  // is reachable from the public site, and none of it may be reused there.
  'recog.title': 'This week’s recognition',
  'recog.sub': '{paper} · {n} of {total} reached {pct}%',
  'recog.sub_junior': '{paper} · {n} of {total} did really well',
  'recog.you': 'you',
  'recog.private': 'Only families signed in to Career Academy can see this. It is never shown on the public website.',
  'recog.and_more': 'and {n} more',

  // --------------------------------------------------------------- rewards
  'rewardcard.earn_title': 'Earn a free month',
  'rewardcard.monthly_title': 'Monthly reward',
  'rewardcard.monthly_sub': '80% across a whole month earns the next month free.',
  'rewardcard.student': 'Student',
  'rewardcard.month': 'Month',
  'rewardcard.score': 'Score',

  // ------------------------------------------------------- billing handover
  'handover.title': 'Let them pay for themselves',
  'handover.action': 'Hand over billing',
  'handover.working': 'Handing over…',

  // ---------------------------------------------------------- subscription
  'sub.verifying': 'Verifying with the gateway…',
  'sub.plan': 'Plan',
  'sub.state': 'State',
  'sub.covers': 'Covers',
  'sub.renews': 'Renews',
  'sub.amount': 'Amount',
  'sub.month_free': 'This month is free.',
  'sub.access_ok': 'Test access permitted.',
  'sub.access_locked': 'Test locked until a payment is verified server-side.',
  'sub.from': 'From',
  'sub.to': 'To',
  'sub.reason': 'Reason',
  'sub.when': 'When',

  // -------------------------------------------------------------- history
  'history.title': 'Tests',
  'history.for_class': 'Class {n}',
  'history.every_test': 'Test history',
  'history.every_test_sub': 'Scores appear once the whole class has been released.',
  'history.none': 'No tests taken yet.',
  'history.none_match': 'No test matches these filters. Widen them to see more.',
  'history.count': '{total} tests',
  'history.count_filtered': '{shown} of {total} tests',
  'history.not_submitted': 'Not submitted',
  'history.ran_out': 'ran out of time',
  'history.review': 'Review',
  'history.across_n': 'Across {n} released tests.',
  'history.all_time': 'Every released test, not only the filtered ones.',
  'history.right_out_of': '{correct} right out of {attempted}',
  'history.five_question_floor':
    'A subject appears here only after at least five questions on it — one or two is not '
    + 'enough to call something a strength or a weakness.',

  // The filters over the test list.
  'history.f_name': 'Test name',
  'history.f_name_hint': 'Search…',
  'history.f_from': 'From',
  'history.f_to': 'To',
  'history.f_status': 'Result',
  'history.f_all': 'All',
  'history.f_out': 'Result out',
  'history.f_waiting': 'Awaiting release',
  'history.f_clear': 'Clear filters',
  'combo.clear': 'Clear this field',
  'combo.no_match': 'No test by that name',
  'history.what_to_work_on': 'What to work on',
  'history.needs_practice': 'Needs practice',
  'history.going_well': 'Going well',
  'history.by_subject': 'By subject',
  'history.right': 'Right',
  'history.wrong': 'Wrong',
  'history.skipped': 'Skipped',
  'history.accuracy': 'Accuracy',

  // -------------------------------------------------------------- support
  'support.with_team': 'With our team',
  'support.replied': 'Replied — over to you',
  'support.resolved': 'Resolved',
  'support.title': 'Help and support',
  'support.sub':
    'Message our team about billing, a test that went wrong, or anything else.',
  'support.not_about_child': 'Not about a specific child',
  'support.sending': 'Sending…',
  'support.conversation': 'Conversation',

  'support.closed': 'Closed',
  'support.about_what': 'What is it about?',
  'support.subject_ph': 'e.g. Invoice for August',
  'support.which_child': 'Which child?',
  'support.optional': '· optional',
  'support.your_message': 'Your message',
  'support.message_ph': 'Tell us what happened. Include dates and names where you can.',
  'support.send': 'Send',
  'support.all_conversations': '← All conversations',
  'support.about_child': 'About {name}',
  'history.subject': 'Subject',

  'support.you': 'You',
  'support.new_count': '{n} new',
  'support.started_by': 'Started by {name}',
  'support.closed_notice':
    'This conversation is closed. Start a new one and we will pick it up.',
  'support.reply': 'Reply',

  // ------------------------------------------------------------- scorecard
  'sc.title': 'Scorecard',
  'sc.student': 'Student',
  'sc.test': 'Test',
  'sc.note': 'Note',
  'sc.correct': 'Correct',
  'sc.wrong': 'Wrong',
  'sc.unanswered': 'Unanswered',
  'sc.auto_submitted': 'Auto-submitted when time expired',
  'sc.by_subject': 'By subject',
  'sc.subject': 'Subject',
  'sc.score': 'Score',
  'sc.download_failed': 'Download failed',
  'sc.preparing': 'Preparing…',
  'sc.download_pdf': 'Download PDF',
  'sc.page_title': 'Test results',
  'sc.page_sub': 'Your score, and every question with its answer.',
  /* The encouragement band. Four bands rather than a single "Good job",
     because the same two words over 35% and over 95% is a platitude a child
     learns to ignore. None of them is discouraging: a low score names the
     next action instead of the shortfall. */
  'sc.praise_high': 'Outstanding!',
  'sc.praise_good': 'Good job!',
  'sc.praise_ok': 'Solid work.',
  'sc.praise_low': 'Keep going.',
  'sc.praise_body': 'You scored {score} out of {max}.',
  'sc.of_max': 'of {max}',
  'sc.subject_score': '{correct} of {max}',

  // ---------------------------------------------------------- intelligence
  'intel.direction': 'Learning insights',
  'intel.direction_sub':
    'Marks over time, beside the average of comparable students on the same paper.',
  'intel.class_average': 'Class average',
  // Was hard-coded English in the student's copy of this card, so the Nepali
  // edition lost it; and the pill was hard-coded in both copies.
  'intel.early_reading': 'early reading',
  'intel.chart_alt': 'Marks over time, against the average of comparable students on the same paper.',
  'intel.showing_up': 'Showing up, and steadiness',
  'intel.compared': 'Compared with others',
  'intel.what_next': 'What next',
  'intel.what_next_sub':
    'Suggestions name material to practise — they never grade the child.',
  'intel.growth_index': 'Growth Index',

  // ---------------------------------------------------------------- signup
  'signup.dev_mode':
    'Development mode — no email or SMS provider is configured, so no code was sent and this account has been marked verified. Payments run on a mock gateway. '
    + 'Configure a provider in Admin \u2192 Integrations to turn this off.',
  'signup.dob': 'Date of birth', 
  'signup.your_dob': 'Your date of birth',
  'elective.legend': 'Optional subjects',
  'elective.note':
    'Classes 8 to 10 sit 36 marks of core subjects and 14 more from the pair chosen below — 7 marks from each of its two subjects. Staff can change it later if you pick the wrong one.',
  'elective.pair_marks': '7 marks each',
  'elective.additional_mathematics': 'Additional Mathematics',
  'elective.computer_education': 'Computer Education',
  'elective.office_practice_accountancy': 'Office Practice & Accountancy',
  'elective.business_studies': 'Business Studies',
  'elective.none': 'no optional subject set',
  'signup.title': 'Sign up',
  'signup.account_heading': 'Create your account',
  'signup.account_lede': 'Join Career Academy and start your journey today.',
  'signup.step_account': 'Your account',
  'signup.step_verify': 'Verify mobile',
  'signup.step_child': 'Your child',
  'signup.step_register': 'Registration',
  'signup.step_plan': 'Choose a plan',
  'signup.step_pay': 'Payment',
  'signup.unconfirmed':
    'The payment has not been confirmed yet. If money left your account it will be '
    + 'matched within a few minutes — you can close this and check your dashboard.',
  'signup.all_set': 'You are all set',
  'signup.paid': 'Payment received.',
  'signup.paid_invoice': 'Payment received, invoice {no}.',
  'signup.can_sit': '{who} can sit the next weekly test.',
  'signup.you': 'You',
  /* The last step, and the only one the wizard cannot do for them: a child
     sits from their own sign-in, and nothing has created one.

     It no longer opens with "One thing left". This ending is not the only one
     that shows it — the registration ending does too, above its own "choose a
     plan" line — and two notices each claiming to be the one thing left is a
     page arguing with itself. */
  'signup.next_give_login':
    'Give {who} a sign-in of their own. A test is sat from the student’s own '
    + 'account, not yours — open your dashboard, find them under Your children, '
    + 'and choose "Give them a sign-in".',
  'signup.receipt_note':
    'We have emailed you a receipt. Before each renewal we will send a reminder by SMS — '
    + 'nothing is ever taken from your account automatically.',
  'signup.go_dashboard': 'Go to my dashboard',
  'signup.acct_self_sub': 'One account: you pay for it and you sit the tests on it.',
  'signup.acct_parent_sub':
    'You are the account holder — your child gets their own login later.',
  'signup.i_am_parent': 'I am a parent',
  'signup.i_am_student': 'I am a student, signing up for myself',
  'signup.i_am_student_short': 'I am a student',
  'signup.role_parent_desc': "I'm creating an account for my child",
  'signup.role_student_desc': 'Signing up for myself',
  'signup.your_full_name': 'Your full name',
  'signup.your_class': 'Your class',
  'signup.select': 'Select',
  'signup.class_n': 'Class {n}',
  'signup.your_stream': 'Your group',
  'signup.your_school': 'Your school',
  'signup.guardian_title': 'Your parent or guardian',
  'signup.guardian_intro':
    'They hold the account: the tests are billed to them, reminders go to their '
    + 'phone, and they accept the agreement below. You keep your own sign-in above.',
  'signup.guardian_name': "Parent or guardian's full name",
  'signup.guardian_email': "Parent or guardian's email",
  'signup.guardian_email_note':
    'They sign in with this, so it has to be different from yours.',
  'signup.guardian_mobile': "Parent or guardian's mobile",
  'signup.guardian_password': 'A password for them',
  'signup.guardian_password_note':
    'Set one now and tell them — they can change it once they sign in.',
  'signup.your_login': 'Your own sign-in',
  'signup.enrolled_heading': 'Enrolled so far',
  'signup.child_class_line': 'Class {n}',
  /* The child's own sign-in, asked on the child step. A test is sat from the
     student's account and not the parent's, so this is the difference between
     a family who can sit Saturday's paper and one who cannot — which is why
     the heading says what it is FOR rather than calling it an account. */
  'signup.child_login_heading': 'How your child signs in',
  'signup.child_login_note':
    'A test is sat from your child’s own account, not yours. Choose their '
    + 'sign-in now and tell them — they can change the password later.',
  'signup.child_login_email': 'Their email',
  'signup.child_login_password': 'A password for them',
  'signup.signs_in_as': 'Signs in as {email}',
  'signup.no_login_yet': 'No sign-in yet — they need one to sit a test',
  'signup.set_their_login': 'Set their sign-in',
  'signup.save_login': 'Save sign-in',
  'signup.add_another': 'Add another child',
  'signup.continue_to_plan': 'Continue to plan',
  'signup.continue_to_registration': 'Continue to registration',
  'signup.next_child_hint':
    'Fill this in for your next child, or continue. The joining fee is charged '
    + 'once per child, and siblings go on one subscription.',
  'signup.plans_for_children': 'Plans that cover all {n} children.',
  'signup.email': 'Email',
  'signup.mobile': 'Mobile',
  'ph.password_current': 'Your current password',
  'ph.password_signin': 'Enter your password',
  'ph.password_new': 'Create a new password',
  'ph.password_confirm': 'Type it again',
  'ph.password_for_them': 'Create a password for them',
  'ph.guardian_name': 'Their full name',
  'ph.guardian_email': 'Their email address',
  'ph.school': 'School or college name',
  'ph.section': 'A, B, C…',
  'ph.child_name': 'Your child\u2019s full name',
  /* Deliberately NOT the parent's address. `users.email` is unique and the
     server refuses a duplicate; the placeholder says so before the refusal
     does. */
  'ph.child_email': 'Their own email address',
  'ph.child_password': 'At least 10 letters and numbers',
  'ph.your_name': 'Your full name',
  'ph.code': '000000',
  'ph.invite_code': 'The code from your invitation',
  'ph.message': 'Tell us what you would like to know',
  'signup.mobile_ph': '98XXXXXXXX',
  'signup.sms_note':
    'Test and renewal reminders come by SMS, so this one has to be reachable.',
  'signup.password': 'Password',
  /* TEN, because ten is what `assertPasswordStrength` enforces. This said
     twelve from the day it was written and the rule never did — so a password
     the server accepts was described on screen as too short. */
  'signup.address_legend': 'Where the family lives',
  'signup.optional': 'optional',
  'signup.province': 'Province',
  'signup.province_ph': 'Select a province',
  'signup.permanent_address': 'Permanent address',
  'signup.permanent_address_ph': 'Ward, municipality, district',
  'signup.local_address': 'Local address',
  'signup.local_address_ph': 'Where you live now, if different',
  'signup.password_hint': 'At least 10 characters, with letters and numbers.',
  'signup.ph_full_name': 'Enter your full name',
  'signup.ph_email': 'Enter your email address',
  'signup.ph_password': 'Create a password',
  'signup.continue_with_google': 'Continue with Google',
  'auth.google_or': 'or',
  'auth.google_expired':
    'That sign-in took too long and has expired. Please try again.',
  'auth.google_failed':
    'We could not sign you in with Google. Use your email and password, or try again.',
  'auth.google_unavailable':
    'Google sign-in is not set up on this site. Please use your email and password.',
  'google.title': 'Sign in with Google',
  'google.sub': 'Use your Google account instead of typing a password.',
  'google.not_connected':
    'Not connected. Connect it and you can sign in with one tap next time.',
  'google.connected_as': 'Connected to {email}.',
  'google.connect': 'Connect Google',
  'google.leaving': 'Taking you to Google…',
  'google.disconnect': 'Disconnect',
  'google.outcome_taken':
    'That Google account is already connected to a different Career Academy '
    + 'account. Sign in to that one, or use a different Google account.',
  'google.outcome_expired':
    'That took too long and has expired. Try connecting again.',
  'google.outcome_failed':
    'We could not reach Google just then. Try connecting again.',
  'google.outcome_unavailable':
    'Google cannot be connected to this account.',
  'google.needs_password_first':
    'Google is the only way into this account, so disconnecting it would lock '
    + 'you out. It can be disconnected once the account has a password as well.',
  'google.disconnect_title': 'Disconnect Google?',
  'google.disconnect_body':
    'You will sign in with your email and password instead. You can connect it '
    + 'again whenever you like.',
  'signup.google_or': 'or fill in the form below',
  'signup.google_signing_up_as':
    'Signing up as {email} with Google. No password needed — you will sign in '
    + 'with the same Google account.',
  'signup.agree_before': 'I have read',
  'signup.agree_after':
    'and I agree to Career Academy recording this information about my family.',
  'signup.hide_it': 'Hide it',
  'signup.read_it': 'Read it',
  'signup.creating': 'Creating…',
  'signup.create_account': 'Create my account',
  'signup.verify_title': 'Verify your mobile',
  'signup.verify_sub': 'We sent a six-digit code by SMS.',
  'signup.dev_before': 'Development mode — your code is',
  'signup.dev_after': '. A production build never shows this.',
  'signup.code': 'Code',
  'signup.verify_btn': 'Verify',
  'signup.resend': 'Send it again',
  'signup.resend_in': 'Send it again in {s}s',
  'signup.send_code': 'Send code',
  'signup.mobile_verified': 'Mobile verified.',
  'signup.email_verified': 'Email verified.',
  /* Heard after "Send code", never seen: the button sits beside its own box,
     so its place says what it sends to — but two "Send code" buttons on one
     form are the same button to a screen reader. (The sentences that used to
     sit beside stacked buttons, "Send a code to your email…", went with them.) */
  'signup.sends_to_email': 'to this email address',
  'signup.sends_to_mobile': 'to this mobile number',
  /* Under the address, where the SMS note sits under the number. Says why the
     code is being asked for, so it reads as a step rather than an obstacle. */
  'signup.email_note':
    'We send a code here to check the address is yours. Receipts and results go '
    + 'to it too, so it has to be one you read.',
  'signup.code_not_sent':
    'We could not send the code just now. Please try again in a moment, or contact us if it keeps failing.',
  'signup.code_wrong': 'That code is not correct. Check it and try again.',
  /* Three sentences, because the server now says WHICH field is taken. One of
     them used to answer for all three, and told people who had reused a mobile
     number that their email was the problem. See lib/signup-collision.ts. */
  'signup.already_registered': 'You already have an account with this email.',
  'signup.mobile_registered':
    'This mobile number is already on an account. Sign in with the email you '
    + 'used, or enter a different number.',
  'signup.both_registered':
    'This email and this mobile number are both already on an account.',
  'signup.sign_in_instead': 'Sign in',
  'signup.verify_note':
    'This step is not optional: renewals in Nepal depend on a reminder reaching you, '
    + 'so we confirm the number before taking any money.',
  'signup.child_sub': 'You can add more children later from your dashboard.',
  'signup.full_name': 'Full name',
  'signup.class': 'Class',
  'signup.section': 'Section',
  'signup.school': 'School',
  'signup.continue': 'Continue',
  'signup.plans_for_class': 'Plans available for Class {n}.',

  /* ------------------------------------------------------- registration
     The joining fee, now its own step and its own payment. The copy has one
     job the old combined invoice never needed: saying that this admits the
     child and the plan is a separate decision — otherwise a parent who has
     just paid reasonably believes they have bought their first month. */
  'signup.register_title': 'Register your family',
  'signup.register_sub':
    'A one-time joining fee, charged once per child. It is what enrols them — '
    + 'choosing a plan comes after, and can wait.',
  'signup.register_who': 'Registering',
  'signup.register_each': 'Joining fee, per child',
  'signup.register_note':
    'Paid once and never again — a renewal never carries it. Your plan is a '
    + 'separate decision, and the next step.',
  'signup.register_unavailable':
    'The joining fee has not been set up yet, so it cannot be taken here. You can '
    + 'still choose a plan — the fee is charged with that first payment instead.',
  'signup.register_skip': 'Choose a plan instead',
  'signup.register_done': 'Everyone on this account is already registered.',
  /* The plan step, which is now optional. Saying what "later" costs is the
     whole point: a parent who skips it has finished registering and still
     cannot sit Saturday's paper. */
  'signup.plan_later': 'I’ll choose a plan later',
  'signup.plan_later_note':
    'Registration is paid either way. A plan is what opens the weekly test, the '
    + 'scorecards and the reports — you can choose one from your dashboard whenever '
    + 'you are ready.',
  'signup.registered_now':
    'Joining fee received — {who} is enrolled. Choosing a plan is the next decision.',
  'signup.registered_title': 'Registration complete',
  'signup.registered_paid': 'Joining fee received for {who}.',
  'signup.registered_paid_invoice': 'Joining fee received for {who}, invoice {no}.',
  'signup.registered_next':
    'Next: choose a plan. It is what opens the weekly test, so until one is chosen '
    + '{who} cannot sit it.',
  'signup.choose_plan_now': 'Choose a plan',

  'signup.finish_payment': 'Finish your payment',
  'signup.pay_waiting':
    'You have a subscription waiting to be paid. Open your dashboard to pay for it — '
    + 'the amount and the plan are shown there.',
  'signup.plan': 'Plan',
  'signup.for': 'For',
  'signup.for_value': '{name} (Class {n})',
  'signup.first_cycle': 'First payment',
  'signup.registration': 'Registration',
  'signup.registration_once': 'once per child',
  // The promotion. Said as what the parent gets, not as what the platform
  // does: "your first {n} tests are free" rather than "a trial is opened".
  /* The public advertisement, on the landing page's fee section and at the
     moment a parent picks a plan. Says what it costs TODAY, because that is
     the number the offer exists to change and the one the fee list cannot
     carry — see lib/promotion.ts. */
  'promo.public_one':
    'Introductory offer: your first test is free. Pay the NPR 100 registration '
    + 'fee to start — the plan fee begins after that.',
  'promo.public_other':
    'Introductory offer: your first {n} tests are free. Pay the NPR 100 registration '
    + 'fee to start — the plan fee begins after that.',
  /* The same offer, to somebody who has just paid the joining fee. The public
     wording sends them to pay it again; this one tells them where they are. */
  'promo.registered_one':
    'Introductory offer: your first test is free. Registration is paid, so your plan '
    + 'fee only begins after it.',
  'promo.registered_other':
    'Introductory offer: your first {n} tests are free. Registration is paid, so your '
    + 'plan fee only begins after them.',

  'signup.promo_free_tests_one': 'Your first test is free.',
  'signup.promo_free_tests_other': 'Your first {n} tests are free.',
  'signup.promo_then_one':
    '{plan} — {amount} begins once your free test has been used.',
  'signup.promo_then_other':
    '{plan} — {amount} begins once your {n} free tests have been used.',
  'signup.promo_remaining_one': '1 free test left',
  'signup.promo_remaining_other': '{n} free tests left',
  'signup.promo_used_up': 'Your free tests have been used.',
  // Feedback on a test just sat. Asked of the child, after their score.
  'fb.title': 'How was this test?',
  'fb.sub': 'A few quick questions. You can skip them.',
  'fb.send': 'Send',
  'fb.sending': 'Sending…',
  'fb.skip': 'Skip',
  'fb.thanks': 'Thank you — that helps us set better papers.',
  'fb.failed': 'That did not send. Your score is safe either way.',
  'signup.amount': 'Amount',
  'signup.vat_included': 'VAT included',
  'signup.pay_esewa': 'Pay with eSewa',
  'signup.pay_khalti': 'Pay with Khalti',
  /* Fonepay is the interoperable QR: the button says what it gets you,
     which is any banking app rather than one particular wallet. */
  'signup.pay_fonepay': 'Scan to pay (any bank)',
  'qr.scan_with_any':
    'Scan this with any bank or wallet app to pay {amount}. It works from '
    + 'mobile banking, eSewa, Khalti and anything else on the {gateway} network.',
  'qr.alt': 'A QR code to scan with your banking app',
  'qr.note':
    'Keep this page open. It updates by itself the moment the payment lands '
    + '\u2014 you do not need to come back and tell us.',
  'qr.waiting': 'Waiting for the payment\u2026',
  'qr.timed_out':
    'Still not paid. If you have sent it, it will appear within a few '
    + 'minutes \u2014 nothing is lost.',
  'qr.cannot_check':
    'We cannot reach the server to check just now. If you have paid, it will '
    + 'still be picked up.',
  'qr.image_failed': 'The QR could not be drawn. Please try another payment method.',
  'qr.paid': 'Paid \u2014 thank you.',
  'qr.paid_invoice': 'Paid \u2014 thank you. Invoice {no}.',
  'signup.pay_note':
    'Nothing is charged again automatically. Before each renewal we send a reminder '
    + 'and you decide.',
  /* Shown instead of the pay buttons on an install with no gateway. Says what
     to do next rather than only what is broken — a parent who has filled in
     the whole wizard needs a way to finish, not an apology.

     Two versions, because the two places it appears are not the same moment:
     in the wizard the worry is that the form is lost, and on the dashboard
     there is no form to lose. See `sub.pay_unavailable`. */
  'signup.pay_unavailable':
    'Online payment is not available yet. Please contact the office to pay — '
    + 'everything you have entered is saved, and we will activate the subscription '
    + 'as soon as the payment is received.',

  /* The same notice inside the Android app, where no checkout is offered at
     all — Google Play requires its own billing for subscriptions sold in an
     app (see lib/app-shell). Deliberately no link or directions to another way
     of paying: Play's policy counts that as steering. */
  'app.pay_on_web': "Pay on the website",
  'app.pay_on_web_note': "Payment opens in your phone's browser, where you are already signed in. Come back to the app when it is done.",
  'app.pay_unavailable':
    'Payments are not taken in the Android app.',

  'common.skip_to_content': 'Skip to content',

  'auth.tagline': 'Weekly Assessment Platform',

  // ------------------------------------------------------------ renew link
  'renew.title': 'Renew your subscription',
  'renew.covered_until': 'Covered until',
  'renew.no_password': 'No password needed — this link can only pay for this subscription.',
  'renew.starting': 'Taking you to {gateway}…',
  'renew.invalid': 'This link has expired or is not valid.',
  'renew.sign_in_instead': 'Sign in to renew from your account',
  'renew.see_everything': 'Sign in to see results and more',
  'renew.pending_retry': 'Check again',
  'renew.reason_cancelled': 'This subscription has been cancelled. Sign in to start a new one.',
  'renew.reason_mobile_unverified':
    'Please verify your mobile number first — sign in to do that, then renew from your account.',
  'renew.reason_covered_by_reward':
    'This cycle is covered by a monthly reward, so there is nothing to pay.',
  'renew.reason_already_renewed': 'Already renewed — covered until {date}.',

  // --------------------------------------------------------------- billing
  'billing.title': 'Billing',
  'billing.due_by': 'Due by',
  'billing.covered_until': 'Covered until',
  'billing.test_access': 'Test access',
  'billing.access_open': 'Open',
  'billing.access_locked': 'Locked',
  'billing.pay_below': 'Pay from the subscription card below, with Khalti.',
  'billing.no_subscription': 'No subscription yet.',
  /* The way to the yearly plan for a family who is paid up. The chooser it
     reveals was only ever shown when money was owed TODAY, which hid it
     from exactly the people free to change their mind. */
  'billing.pay_early': 'Pay early or change to yearly',
  /* One cadence per checkout, which is the server's rule and not a
     limitation of this page: one amount cannot stand for two period
     lengths. Reachable only since a parent could put some children on
     monthly and others on yearly. */
  'billing.one_cycle_only':
    'These are on different billing cycles, so they are paid for separately. '
    + 'Choose the monthly ones or the yearly ones, then do the rest.',
  // The four tabs (one Billing page, signposted rather than one long scroll).
  'billing.tab_due': 'Due now',
  'billing.tab_plans': 'Subscriptions',
  'billing.tab_invoices': 'Invoices',
  'billing.tab_activity': 'Activity',
  'billing.activity': 'Account activity',
  'billing.activity_sub': 'Every change to a subscription, newest first — who paid, what lapsed, what was reinstated.',
  'billing.no_activity': 'Nothing has changed on your subscriptions yet.',
  'billing.history': 'What you have paid',
  'billing.no_history': 'No payments yet.',
  'billing.invoice': 'Invoice',
  'billing.when': 'When',
  'billing.what_for': 'For',
  'billing.paid_with': 'Paid with',
  'billing.amount': 'Amount',
  'billing.at_the_office': 'At the office',
  'billing.show_all': 'Show all {n}',
  'billing.show_recent': 'Show recent only',

  'billing.page_sub': 'What you owe, who it covers, and everything you have paid.',
  'billing.due_title': 'Due now',
  'billing.due_sub': 'Choose who to pay for. Everything owed is ticked; untick anyone you are not paying for this time.',
  'billing.nothing_owed': 'Nothing is due right now.',
  'billing.no_children': 'No child on this subscription',
  'billing.covers_to': 'covers to {date}',
  'billing.includes_registration': 'includes {amount} to register {names}, once only',
  'billing.selected_total': '{n} selected',
  'billing.redirecting': 'Taking you to {gateway}…',
  'billing.paid': 'Payment received, invoice {no}.',
  'billing.verify_mobile_first': 'Please verify your mobile number before paying — renewal reminders are sent by SMS.',
  'billing.subscriptions': 'Your subscriptions',
  'billing.subscriptions_sub': 'One per class group, because plans are priced by group.',
  'billing.child': 'Child',
  'billing.next_amount': 'Next payment',
  'billing.free': 'Free',
  'billing.history_sub': 'Every invoice on this account.',

  'billing.how_often': 'How often would you like to pay?',
  'billing.cycle_monthly': 'Monthly',
  'billing.cycle_quarterly': 'Every three months',
  'billing.cycle_annual': 'For the year',
  'billing.save_amount': 'Save {amount}',
  'billing.per_month': '{amount} a month',

  // ------------------------------------------------------- performance line
  'perf.up': '{name} is improving',
  'perf.up_self': 'You are improving',
  'perf.down': '{name} is slipping',
  'perf.down_self': 'You are slipping',
  'perf.steady': '{name} is holding steady',
  'perf.steady_self': 'You are holding steady',
  'perf.early': 'Too early to read a direction — the first few Saturdays will show one',
  'perf.provisional': 'so far',
  'perf.weakest': 'weakest in {subject} ({correct} of {asked})',
  'perf.next': '{advice}',
  // The character a sentence closes with. Nepali ends in a danda, so this is a
  // string and not a `.` in the code — see `performance-headline.ts`.
  'perf.stop': '.',

  // -------------------------------------------------------------- security
  'security.devices': 'Signed-in devices',
  'security.devices_sub': 'Every browser currently able to reach your account.',
  'security.device': 'Device',
  'security.signed_in': 'Signed in',
  'security.none_other': 'No other active sessions.',
  'security.signs_out_all': 'This signs out every device, including this one.',
  'security.device_on': '{browser} on {os}',
  'security.unknown_app': 'An unrecognised app',
  'security.unknown_system': 'an unknown system',
  'security.a_browser': 'A browser',

  // -------------------------------------------------------------- password
  'password.title': 'Password',
  'password.changed': 'Password changed.',
  'password.sign_in_again': 'Sign in again',
  'password.signs_out_sub': 'Changing it signs out every device, including this one.',
  'password.too_short': 'Use at least 10 characters.',
  'password.needs_both': 'Include both letters and numbers.',

  // ---------------------------------------------------------- answer review
  'review.answers': 'Answers',
  'review.every_question': 'Every question',
  'review.right': 'Right',
  'review.wrong': 'Wrong',
  'review.not_answered': 'Not answered',
  'review.answered': 'Answered',
  'review.title': 'Question review',
  'review.sub': 'Look at each question, see the correct answer and your answer.',
  'review.filter_all': 'All questions',
  'review.filter_wrong': 'Only wrong',
  'review.filter_unanswered': 'Only unanswered',
  'review.none_match': 'Nothing to show with that filter.',
  'review.your_answer': 'your answer',
  'review.expand': 'Show the options',
  'review.collapse': 'Hide the options',

  // -------------------------------------------------------- student screens
  'insights.what_next': 'What to do next',
  'insights.what_practise': 'What to practise',
  'insights.what_practise_sub':
    'Across every paper you have sat, not just the last one — which is why this '
    + 'can name a different subject from your latest result.',
  'insights.how_doing': 'How you are doing',
  'insights.how_doing_sub':
    'Your marks over time, beside the average of students on the same paper.',
  'insights.turning_up': 'Turning up, and staying steady',
  'student.no_student': 'No student selected',
  'student.not_linked': 'This account is not linked to a student profile.',
  'student.after_that': 'After that',
  'student.after_that_sub': 'The Saturdays already on the timetable.',
  'student.your_next_test': 'Your next test',

  // ------------------------------------------------------------ my account
  'account.my_profile': 'My profile',
  'account.how_we_contact': 'How we contact you',
  'account.mobile_ph': '9801234567',
  'account.code_ph': '000000',

  // ------------------------------------------------------- subscription card
  'sub.redirecting': 'Taking you to {gateway}…',
  'sub.pay_verified': 'Payment verified. Invoice {no} issued.',
  /* The dashboard's version of `signup.pay_unavailable`. No form is in flight
     here, so it says nothing about saved details — it says how to pay. */
  'sub.pay_unavailable':
    'Online payment is not available yet. Please contact the office to pay and '
    + 'we will update your subscription.',
  'sub.see_activity': 'See every change to this subscription',

  // ---------------------------------------------------------- the analysis
  // Rebuilt on the client from the server's codes and figures, because a
  // sentence composed on the server can only ever be in one language.
  'an.up': 'Up {points} marks since the start — the recent papers are going better than the early ones.',
  'an.down': 'Down {points} marks since the start. Worth looking at which subject slipped rather than assuming it is all of them.',
  'an.steady': 'Holding steady. The scores move about a little, but there is no real trend either way yet.',
  'an.early_reading': 'Said from three papers, so treat it as early rather than settled.',
  'an.spread': 'Across the last {papers} papers, scores stayed within {points} points of each other.',
  'an.spread_early': 'Week-to-week consistency needs four papers to say anything — there are {papers} so far.',
  'an.no_comparison':
    'Comparisons with other students are not shown yet. They arrive as more of the class sits each paper.',
  'an.r0_one': 'One result so far — after the next Saturday there will be enough to say which way this is moving.',
  'an.r0_many': '{papers} results so far — after the next Saturday there will be enough to say which way this is moving.',
  'an.r1': 'This week, practise {subject} — {correct} of {attempted} right across all papers.',
  'an.r2': 'Revisit {subject} — recent papers slipped there.',
  'an.r3': 'Keep the routine; for stretch, try harder practice in {subject}.',
  'an.r4': 'Aim to sit every Saturday — the trend can only be read from papers actually sat.',
  'an.r5': 'Scores swing week to week; short regular practice tends to steady them.',
  'an.r5_junior': 'Results move about from week to week; short, regular practice tends to steady them.',
  'an.r6': 'Practise timed attempts in {subject} — many questions were left unanswered.',
  'an.r7': 'Keep the weekly rhythm — nothing needs special attention this week.',

  // The gaps. Everything above says again what the server decided; these are
  // the client's own words for the cases where the server decided nothing —
  // not enough papers to read a direction, no subject worth naming, the
  // attendance count, the billing note. They were literals in
  // `intelligence-view.ts`, which put an English reading inside a Nepali card.
  'an.no_results': 'No results yet — the first Saturday will start the line.',
  'an.no_direction': 'Too early to read a direction. A couple more Saturdays will show one.',
  'an.series_self': 'Your score',
  'an.series_other': 'Their score',
  'an.practise_junior':
    'Practice for Class 1-3 comes from the teacher\'s notes rather than a chart.',
  'an.practise_none':
    'No subject has enough evidence to need special work — keep the weekly rhythm.',
  'an.no_tests': 'No tests offered yet.',
  'an.sat_of': 'Sat {sat} of {offered} Saturdays offered.',
  'an.not_covered_one': '1 Saturday not covered by the subscription.',
  'an.not_covered_many': '{count} Saturdays not covered by the subscription.',
  'an.needs_work_row': '{correct} of {attempted} correct across all papers',
  'an.needs_work_row_self': '{correct} of {attempted} right across all papers',

  // ------------------------------------------------------------ exam runner
  'exam.question_n_of': 'Question {n} of {total}',
  'exam.resumed': 'Resumed — your answers and remaining time were restored from the server',
  'exam.linear_hint': 'One question at a time. You cannot return to a question once you move on.',
  'exam.autosave_hint': 'Answers save automatically as you go',
  'exam.q_marks_one': 'Question {n} · 1 mark',
  'exam.q_marks_other': 'Question {n} · {marks} marks',
  'exam.previous': 'Previous',
  'exam.next': 'Next',
  // The Class 1-3 hand-off (B1). Short on purpose: the grown-up reading it is
  // usually standing up, and the child beside them is six.
  'exam.handoff_title': 'Ready when you are',
  'exam.handoff_body': 'Sit with your child first. The timer starts when you tap Begin, not before.',
  'exam.handoff_length': 'The test takes about {minutes} minutes.',
  'exam.handoff_begin': 'Begin the test',
  'exam.handoff_not_yet': 'Not just now',
  'exam.mark': 'Mark for review',
  'exam.unmark': 'Unmark',
  'exam.submit': 'Submit test',
  'exam.submitting': 'Submitting…',
  'exam.choose_to_continue':
    'Choose an answer to continue. You cannot return to this question later.',
  'exam.confirm_title': 'Submit your test?',
  'exam.confirm_all_done':
    'You have answered every question. You cannot change your answers after this.',
  'exam.confirm_unanswered_one':
    '1 question is unanswered. You cannot go back to it once you submit.',
  'exam.confirm_unanswered_other':
    '{n} questions are unanswered. You cannot go back to them once you submit.',
  'exam.not_sent': 'Your test has not been sent yet.',
  'exam.back_to_dashboard': 'Back to dashboard',

  // Who may sit a paper: the student, and only the student. A parent holds
  // every other permission over this child and not this one, so the sentence
  // has to explain rather than refuse — a parent reading "not permitted" about
  // their own child on a Saturday morning rings the office.
  'sit.student_signs_in': '{name} sits this test from their own sign-in.',
  'sit.needs_login': '{name} has no sign-in yet, and needs one to sit this test.',
  'sit.give_login': 'Give them a sign-in',
  'sit.not_yours': 'This test is not yours to sit.',
  'sit.parent_blocked_title': 'This test is sat by the student',

  // Leaving the paper. Every one of these had to be written twice: the first
  // drafts said "this has been reported" and "do not leave again", which is a
  // machine accusing a child of cheating for answering the door. Nothing here
  // cancels, scores down or flags anybody — the sentence is the whole feature,
  // so it says exactly what happens and no more.
  'exam.away_first':
    'You left the test. The time you were away is noted on your paper. '
    + 'Please stay on this page until you finish.',
  'exam.away_again':
    'You have left the test {n} times. The time you were away is noted on your paper. '
    + 'Please stay on this page until you finish.',
  'exam.away_dismiss': 'Continue the test',
  // Fullscreen. Offered, never forced — and never offered at all on a device
  // that cannot do it, which is every iPhone.
  'exam.fs_enter': 'Take this test in fullscreen so nothing else is on the screen.',
  'exam.fs_go': 'Go fullscreen',
  'exam.fs_left': 'You left fullscreen. That is noted on your paper.',
  'exam.fs_back': 'Return to fullscreen',
  'exam.fs_dismiss': 'Not now',

  // Whole sentences per case, not assembled fragments: Nepali does not split
  // "1 answer is" / "2 answers are" the way English does.
  'save.all_saved': 'All answers saved.',
  'save.saving_one': 'Saving your answer…',
  'save.saving_other': 'Saving {n} answers…',
  'save.failed_one':
    'Your answer is not saving. It is kept on this device and will be sent as soon as '
    + 'the connection allows.',
  'save.failed_other':
    '{n} answers are not saving. They are kept on this device and will be sent as soon '
    + 'as the connection allows.',
  'save.offline_free_none': 'You are offline. Keep answering — nothing is lost.',
  'save.offline_free_one':
    'You are offline. Your answer is kept on this device and will be sent when your '
    + 'connection returns.',
  'save.offline_free_other':
    'You are offline. {n} answers are kept on this device and will be sent when your '
    + 'connection returns.',
  'save.offline_linear_none':
    'You are offline. This test moves one question at a time, so it cannot continue '
    + 'until your connection returns. Nothing you have done is lost.',
  'save.offline_linear_one':
    'You are offline. This test moves one question at a time, so it cannot continue '
    + 'until your connection returns. Your answer is safe on this device.',
  'save.offline_linear_other':
    'You are offline. This test moves one question at a time, so it cannot continue '
    + 'until your connection returns. {n} answers are safe on this device.',

  // ----------------------------------------------------------------- reward
  'reward.headline': 'Score 80% or more this month and next month is free.',
  'reward.body':
    'It counts across every Saturday in the month, not one good paper — and a test you '
    + 'miss is scored as zero. Turning up matters as much as the marks.',

  // ------------------------------------------------------------------- tour
  /* The first-run tour. The copy IS the feature: the machinery around it is a
     dialog with two buttons, and everything worth building is in these four
     short paragraphs. See docs/specs/2026-09-28-first-run-tour-design.md. */
  'tour.title': 'Let me show you around',
  'tour.step_of': 'Step {n} of {total}',
  'tour.back': 'Back',
  'tour.next': 'Next',
  'tour.done': 'Done',
  'tour.skip': 'Skip',
  'tour.replay': 'Show me around',
  /* No step count. A replay is as long as the family still has steps for —
     three once the joining fee is paid — and "four-step" was a promise the
     dialog behind it did not keep. */
  'tour.replay_sub': 'The short tour of this dashboard, again.',
  'tour.replay_cta': 'Start the tour',

  'tour.step_pending_title': "What's still to do",
  'tour.step_pending_body':
    'Anything standing between your child and Saturday shows up here: the joining '
    + "fee first, then a plan. When this card is gone, they're ready.",

  'tour.step_children_title': 'Each child needs their own sign-in',
  'tour.step_children_body':
    "A test is sat from the student's own account, not yours. Find them under Your "
    + 'children and choose "Give them a sign-in" — without one they cannot sit '
    + "Saturday's paper.",

  'tour.step_papers_title': 'The Saturday paper',
  'tour.step_papers_body':
    'Every Saturday a paper opens for a set window and closes on its own. A paper '
    + 'not sat inside its window is scored zero, so the SMS reminder is worth reading.',

  'tour.step_scorecard_title': 'What arrives afterwards',
  'tour.step_scorecard_body':
    "Once Saturday's paper is marked, this dashboard fills in: the scorecard, the "
    + 'subject-by-subject breakdown, and the trend across papers. Empty today only '
    + "means the first Saturday hasn't happened yet.",
} as const;

export type StringKey = keyof typeof en;
